<script setup async>
import {ref, computed, onMounted, watch} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import {useInviteLink} from '~/composables/useInviteLink';
import {useHeadService} from '~/composables/useHeadService';
import {useFormData} from '~/composables/useFormData';
import LoginForm from '~/components/auth/LoginForm.vue';
import RegisterForm from '~/components/auth/RegisterForm.vue';
import {useMobileApp} from '~/composables/useMobileApp';
import AuthCard from '~/components/auth/AuthCard.vue';
import PayEntryFeeDialog from '~/components/payments/PayEntryFeeDialog.vue';
import {usePaymentsDisplay} from '~/composables/usePaymentsDisplay';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useJoinRequestStatus} from '~/composables/useJoinRequestStatus';
import {endpoints} from '~/network/endpoints';
import CompetitionQuestionList from '~/components/competitions/CompetitionQuestionList.vue';
import CompetitionQuestionsPrompt from '~/components/competitions/CompetitionQuestionsPrompt.vue';
import {competitionTypeForInvite, createJoinQuestions, pendingQuestions} from '~/composables/useCompetitionQuestions';
import {useMeetFormat} from '~/composables/useMeetFormat';
import {
  entryFeeJoinBlocked,
  entryFeeJoinFields,
  entryFeeJoinHeld,
  heldJoinFeeLine,
  entryFeeJoinNote,
  entryFeeJoinTerms,
  entryFeeJoinTickLabel,
  inviteJoinButtonLabel,
  isEntryFeeTermsChanged,
  joinCancellationLine,
  joinPayeeLine,
} from '~/composables/usePayingHonestly.js';

definePageMeta({
  name: 'InviteJoin',
  path: '/invite/:slug',
});

const route = useRoute();
const router = useRouter();

const {isAuthenticated, user, updateLocalUser, joinWithInviteLink, joinPending, joinError} = useAuthService();
const {
  getInviteLink,
  inviteLink,
  inviteLinkPending,
  hasInviteLinkErrors,
  inviteLinkErrors,
  unlockInviteLink,
  unlockPending,
  unlockError
} = useInviteLink();
const {inviteReferenceTypeToText} = useFormData();
const {setAlertMessage} = useAlertService();
const {uploadFilenameToUrl} = useCrudConverters();

// State
const password = ref(''); // For password unlock step only
const requestMessage = ref('');
// No default: the person says whether they are new or already have an account
// (ACCOUNT_IDENTITY_PLAN.md layer 0); the forms appear only after that.
const inviteChoice = ref(null); // 'signUp' | 'signIn' | null
const nameConfirmed = ref(false); // For player invite name mismatch confirmation

// Load invite data
await getInviteLink(route.params.slug);

// Computed properties
const entityData = computed(() => {
  if (!inviteLink.value) return null;
  switch (inviteLink.value.referenceType) {
    case 0: return inviteLink.value.club;
    case 1: return inviteLink.value.ladder;
    case 2: return inviteLink.value.leagueCycle;
    case 3: return inviteLink.value.player;
    case 4: return inviteLink.value.tournament;
    case 5: return inviteLink.value.meet;
    case 6: return inviteLink.value.americanoCycle;
    default: return null;
  }
});

const isPlayerInvite = computed(() => inviteLink.value?.referenceType === 3 && inviteLink.value?.player);

const entityName = computed(() => {
  if (isPlayerInvite.value) {
    return inviteLink.value.player.fullName || 'Player';
  }
  // Americano cycle: give the cycle its league context ("Americano · Cycle name")
  if (inviteLink.value?.referenceType === 6 && entityData.value) {
    return [entityData.value.americanoName, entityData.value.name].filter(Boolean).join(' · ');
  }
  return entityData.value?.name || '';
});

const clubName = computed(() => inviteLink.value?.clubName || '');
// Already a member of the club (audit N17): a competition link only asks them
// to compete, never to "join <club>" again.
const alreadyInClub = computed(() => {
  // owningClubId is the link's club whatever it points at (clubId is only set on
  // club-level links); ids are compared without case — they arrive in both.
  const clubId = String(inviteLink.value?.owningClubId ?? inviteLink.value?.clubId ?? '').toLowerCase();
  const memberOf = (user.value?.memberOf ?? []).map((id) => String(id).toLowerCase());
  return !!(isAuthenticated.value && clubId && memberOf.includes(clubId));
});

const entityImageUrl = computed(() => {
  const imageFileName = inviteLink.value?.clubImageFileName || entityData.value?.imageFileName || inviteLink.value?.club?.imageFileName;
  if (!imageFileName) return null;
  return uploadFilenameToUrl(imageFileName, 'medium');
});

const entityTypeText = computed(() => {
  if (!inviteLink.value) return '';
  if (isPlayerInvite.value) return 'Player Invite';
  return inviteReferenceTypeToText(inviteLink.value.referenceType);
});

const isPasswordProtected = computed(() => inviteLink.value?.isPasswordProtected || false);

const authId = computed(() => inviteLink.value?.authId || null);

const needsPasswordUnlock = computed(() => isPasswordProtected.value && !authId.value);

const canShowAuthForms = computed(() => !isAuthenticated.value && authId.value !== null);
// Already in (typically auto-joined while confirming the email from an invite
// registration, then sent back here) — offer "open", never a join that fails.
const isAlreadyMember = computed(() => isAuthenticated.value && !!inviteLink.value?.isMember);
// Joined and waiting for an admin to place them — a subset of isMember (JOIN_SETTINGS_PLAN.md D12).
const isAwaitingPlacement = computed(() => isAuthenticated.value && !!inviteLink.value?.isAwaitingPlacement);

const requiresApproval = computed(() => inviteLink.value?.inviteJoinSetting === 1);

// An approval link sends the request by itself when someone signs in or registers
// on this page, so the page has to say a request exists rather than offer a first
// one. Asking again updates that same request — which is how a message is added.
const requestEntityType = {0: 'club', 1: 'ladder', 2: 'leagueCycle', 4: 'tournament', 5: 'meet', 6: 'americanoCycle'}[inviteLink.value?.referenceType] ?? null;
const {isPending: requestIsPending, refresh: refreshRequestStatus} =
    useJoinRequestStatus(requestEntityType, () => inviteLink.value?.referenceId);
const requestSent = computed(() => isAuthenticated.value && requiresApproval.value && requestIsPending.value);

// A draft box league cycle, americano cycle or tournament cannot be joined yet: say so
// up front rather than let Accept fail. (Clubs, ladders and meets have no draft state.)
const isUnpublished = computed(() => {
  const target = {2: inviteLink.value?.leagueCycle, 4: inviteLink.value?.tournament, 6: inviteLink.value?.americanoCycle}[inviteLink.value?.referenceType];
  // Someone an admin has already put in sees "You're already in", not this.
  return !isAlreadyMember.value && target?.published === false;
});

// presentational — initials crest fallback when no image (invite.html)
const crestInitials = computed(() => {
  const name = clubName.value || entityName.value || '';
  return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
});

// Helper function to check if two strings are similar (using Levenshtein-like logic)
const stringSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  if (s1 === s2) return 1;

  // Simple similarity: count matching characters in order
  let matches = 0;
  const minLength = Math.min(s1.length, s2.length);
  for (let i = 0; i < minLength; i++) {
    if (s1[i] === s2[i]) matches++;
  }
  return matches / Math.max(s1.length, s2.length);
};

// Check if user's name is similar to invited player's name
const namesMismatch = computed(() => {
  if (!isPlayerInvite.value || !isAuthenticated.value || !user.value) return false;

  const inviteFirstName = inviteLink.value?.player?.firstName || '';
  const inviteLastName = inviteLink.value?.player?.lastName || '';
  const userFirstName = user.value.firstName || '';
  const userLastName = user.value.lastName || '';

  // Check similarity threshold (0.6 = 60% similar)
  const firstNameSimilar = stringSimilarity(inviteFirstName, userFirstName) >= 0.6;
  const lastNameSimilar = stringSimilarity(inviteLastName, userLastName) >= 0.6;

  // Names mismatch if either first or last name is not similar
  return !firstNameSimilar || !lastNameSimilar;
});

const getEntityRoute = () => {
  if (!inviteLink.value) return '/';
  switch (inviteLink.value.referenceType) {
    case 0: // Club
      return {name: 'ClubDetail', params: {clubId: inviteLink.value.referenceId}};
    case 1: // Ladder
      return {name: 'LadderDetail', params: {ladderId: inviteLink.value.referenceId}};
    case 2: // LeagueCycle
      // Cycle links carry no club object — guard on the cycle itself, or the
      // joiner was bounced to the homepage instead of the league they joined.
      if (inviteLink.value.leagueCycle?.leagueId) {
        return {name: 'LeagueDetail', params: {leagueId: inviteLink.value.leagueCycle.leagueId, cycleId: inviteLink.value.referenceId}};
      }
      return '/';
    case 3: // Player - redirect to club
      if (inviteLink.value.club?.id) {
        return {name: 'ClubDetail', params: {clubId: inviteLink.value.club.id}};
      }
      return '/';
    case 4: // Tournament
      return {name: 'TournamentDetail', params: {tournamentId: inviteLink.value.referenceId}};
    case 5: // Meet — joining makes you a member of the meet itself (the Meet IS the event)
      return {name: 'MeetDetail', params: {meetId: inviteLink.value.referenceId}};
    case 6: // AmericanoCycle — the link references the cycle, like league cycles
      if (inviteLink.value.americanoCycle?.americanoId) {
        return {name: 'AmericanoDetail', params: {americanoId: inviteLink.value.americanoCycle.americanoId, cycleId: inviteLink.value.referenceId}};
      }
      return '/';
    default:
      return '/';
  }
};

// Methods
const handleUnlock = async () => {
  try {
    await unlockInviteLink(inviteLink.value.id, password.value);
    password.value = ''; // Clear password after successful unlock
  } catch (e) {
    // Error is handled by unlockError ref
  }
};

// ── Entry fee: a paid competition requires a saved card before joining ──
const {money, cardLabel} = usePaymentsDisplay();
const {httpRequest} = useHttpRequest();

const entryFeeMinor = computed(() => inviteLink.value?.entryFeeMinor ?? null);
const isPaidJoin = computed(() => entryFeeMinor.value != null);
const entryFeeCurrency = computed(() => inviteLink.value?.entryFeeCurrency || 'gbp');

// The competition entity id (ladder / league cycle / tournament) = obligation sourceId
const feeSourceId = computed(() => inviteLink.value?.referenceId);

// ── Join and pay (owner decision 28 Sep 2026): the button commits to the fee ──
// CCRs reg 14 and 36: the member reads the entry fee terms and ticks them at
// the button that commits them. An API that sends no terms keeps today's flow
// (plain "Accept invitation", the tick asked at the Pay step instead). An
// existing request only takes a message, so it asks nothing further.
const {termsAsHtml} = useMeetFormat();
const joinTerms = computed(() => entryFeeJoinTerms(inviteLink.value));
const feeAmount = computed(() => money(entryFeeMinor.value, entryFeeCurrency.value));
const asksJoinTerms = computed(() => joinTerms.value.asksTerms && !requestSent.value);
const entryFeeTicked = ref(false);
// New terms need a new tick. A number, so only a real change fires.
watch(() => joinTerms.value.termsVersion, () => { entryFeeTicked.value = false; });
// Held for placement: the fee is owed once an admin places them (R2 batch B).
const joinHeld = computed(() => entryFeeJoinHeld(inviteLink.value));
// The club is the seller (CCRs reg 13), and the cancellation position the API
// writes from the competition's dates (reg 13 / Sch 2) sits right above the tick.
// Both absent with no fee, or from an older API.
const joinPayee = computed(() => joinPayeeLine(inviteLink.value));
const joinCancellation = computed(() => joinCancellationLine(inviteLink.value));
const joinButtonLabel = computed(() => inviteJoinButtonLabel({
  requestSent: requestSent.value,
  requiresApproval: requiresApproval.value,
  asksTerms: asksJoinTerms.value,
  amount: feeAmount.value,
  held: joinHeld.value,
}));

// After a paid, non-approval join we offer to settle the new obligation right away
const payFeeOpen = ref(false);
const payFeeObligation = ref(null);

const openNewObligation = async () => {
  try {
    const list = await httpRequest({
      ...endpoints.payments.myEntryFeeObligations,
      path: endpoints.payments.myEntryFeeObligations.path + `?sourceIds=${feeSourceId.value}`,
    });
    payFeeObligation.value = (list || [])[0] || null;
    if (payFeeObligation.value) payFeeOpen.value = true;
  } catch (e) {
    payFeeObligation.value = null;
  }
};

// After a join, the entity page shows the "you're in — get the app" hand-off
// on phones (useMobileApp reads this once and clears it).
const {setPostJoin} = useMobileApp();
const goToEntity = () => {
  const target = getEntityRoute();
  const resolved = router.resolve(target);
  setPostJoin(resolved.fullPath, resolved.name);
  return router.push(target);
};

// ── Competition questions: asked as ticks on the join confirmation ──
// The API decides what is waiting (Squash Levels today); unticked is a no.
// Answering here is part of joining, so the competition page does not ask
// again. Signing in ON this page joins through the link's authId without
// reaching this step; the competition page then asks instead.
const joinCompetitionType = computed(() => competitionTypeForInvite(inviteLink.value?.referenceType));
const {
  questions: joinQuestions,
  ticks: joinTicks,
  load: loadJoinQuestions,
  beginJoin,
  saveAnswers,
  endJoin,
} = createJoinQuestions({
  fetchPending: async () => {
    if (import.meta.server || !isAuthenticated.value || !joinCompetitionType.value || !inviteLink.value?.referenceId) return [];
    if (isAlreadyMember.value || isUnpublished.value) return [];
    return pendingQuestions(await httpRequest(
        endpoints.competitionQuestions.pending(joinCompetitionType.value, inviteLink.value.referenceId, true)));
  },
  putAnswers: async (answers) => {
    await httpRequest(endpoints.competitionQuestions.answer(joinCompetitionType.value, inviteLink.value.referenceId),
        {joining: true, answers});
    await updateLocalUser();
  },
});
onMounted(loadJoinQuestions);
// Only a real change of member or link reloads. A getter returning a new array
// fired on every replacement of the user object, which joining does.
watch([() => user.value?.id, () => inviteLink.value?.id], loadJoinQuestions);

// Signing in ON this page (email, Apple, Google — and the register/verify
// round-trip) auto-joins through the link's authId on the API, then the forms
// route back to this same path, which does not remount. Re-read the link for
// the signed-in account: already in → straight to the entity; otherwise the
// page now offers Accept for the right person.
watch(() => user.value?.id, async (id, was) => {
  if (!id || id === was || !inviteLink.value) return;
  try {
    await getInviteLink(route.params.slug);
  } catch (e) {
    return;
  }
  if (inviteLink.value?.isMember) {
    setAlertMessage(`You're in. Welcome to ${entityName.value}`);
    await goToEntity();
    return;
  }
  // Approval link: the sign-in sent the request. Pick it up so the page shows it.
  if (requiresApproval.value) {
    await updateLocalUser();
    await refreshRequestStatus();
  }
});

const handleJoin = async () => {
  const wasSent = requestSent.value;
  // Captured BEFORE joining: joining replaces the stored user, which reloads
  // the questions, and the answer must not go with them.
  const captured = beginJoin();
  // The acceptance goes with the version it was shown, read before anything reloads.
  const feeFields = entryFeeJoinFields({
    asksTerms: asksJoinTerms.value,
    ticked: entryFeeTicked.value,
    termsVersion: joinTerms.value.termsVersion,
  });
  try {
    const res = await joinWithInviteLink(
      inviteLink.value.id,
      authId.value,
      requiresApproval.value ? requestMessage.value || null : null,
      feeFields
    );
    if (!res) return;
    await saveAnswers(captured);

    if (requiresApproval.value) {
      // Approval-gated: a request is created (fee becomes due only once approved).
      setAlertMessage(wasSent ? 'Message added to your request.' : 'Request sent. An admin will review it shortly.');
      await goToEntity();
      return;
    }

    // A held join (doubles ladder / league cycle / americano) lands in the waiting
    // area: say so, and skip the fee prompt — fees are charged at placement.
    try {
      await getInviteLink(route.params.slug);
    } catch (e) {
      // fall through with the pre-join link
    }
    if (inviteLink.value?.isAwaitingPlacement) {
      setAlertMessage(`You're in. An admin will place you in ${entityName.value}`);
      await goToEntity();
      return;
    }

    setAlertMessage('Successfully joined!');
    if (isPaidJoin.value) {
      // Joined a paid competition — offer to pay the fee now (they can also skip
      // and pay later from the competition page or their payments page).
      await openNewObligation();
      if (!payFeeObligation.value) await goToEntity();
    } else {
      await goToEntity();
    }
  } catch (e) {
    // Error is handled by joinError from useAuthService. The terms moved on
    // while the page was open: load the new ones and ask for a new tick; the
    // server's own sentence stays on screen above the button.
    if (isEntryFeeTermsChanged(e)) {
      entryFeeTicked.value = false;
      try {
        await getInviteLink(route.params.slug);
      } catch (reloadError) {
        // keep the page as it was; the message still says what happened
      }
    }
  } finally {
    endJoin();
  }
};

const onFeePaid = async () => {
  // dialog shows its own success state; navigating happens when they close it
};

// Set page meta
if (inviteLink.value) {
  const {setMeta} = useHeadService();
  const metaTitle = entityName.value
    ? `Join ${entityName.value} | Tap Leagues`
    : 'Join Link | Tap Leagues';
  setMeta(metaTitle, `Join this ${entityTypeText.value?.toLowerCase() || 'group'} on Tap Leagues.`);
}
</script>

<template>
  <base-page>
    <v-container class="auth-page">
      <!-- Loading state -->
      <div v-if="inviteLinkPending" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="48"/>
      </div>

      <!-- Error state -->
      <auth-card
        v-else-if="hasInviteLinkErrors"
        title="Invalid link"
        :subtitle="inviteLinkErrors?.message || 'This join link could not be found or is no longer valid.'"
      >
        <template #icon>
          <div class="invite-crest">
            <v-icon size="28" icon="mdi-link-off"/>
          </div>
        </template>
        <v-btn color="primary" size="large" :block="true" class="mt-4" to="/">Go home</v-btn>
      </auth-card>

      <!-- Invitation card (invite.html — club identity at top, primary accept) -->
      <auth-card v-else-if="inviteLink">
        <template #icon>
          <div v-if="entityImageUrl" class="invite-crest image">
            <v-img :src="entityImageUrl" width="64" height="64" cover/>
          </div>
          <div v-else class="invite-crest">{{ crestInitials || 'TL' }}</div>
        </template>

        <template #hero>
          <!-- Player invite -->
          <template v-if="isPlayerInvite">
            <h1>Hi {{ inviteLink.player.firstName || entityName }}!</h1>
            <div class="invite-chips">
              <span class="invite-chip">{{ entityTypeText }}</span>
            </div>
            <p>
              You've been invited to join <template v-if="clubName"><b class="emphasis">{{ clubName }}</b></template>
            </p>
            <template v-if="isUnpublished || requestSent"/>
            <p v-else-if="isAuthenticated && !isAlreadyMember">Click below to accept your invitation.</p>
            <p v-else-if="needsPasswordUnlock">Enter the password below to continue.</p>
            <p v-else>Sign in or create an account to accept your invitation.</p>
          </template>

          <!-- Standard entity invite -->
          <template v-else>
            <h1>{{ clubName || entityName }}</h1>
            <div class="invite-chips">
              <span class="invite-chip">{{ entityTypeText }}</span>
            </div>
            <p>
              <template v-if="inviteLink.referenceType === 0">
                You've been invited to join <b class="emphasis">{{ clubName }}</b>
              </template>
              <template v-else-if="inviteLink.referenceType === 1">
                <template v-if="clubName && !alreadyInClub">
                  You've been invited to join <b class="emphasis">{{ clubName }}</b> and compete in <b class="emphasis">{{ entityName }}</b>
                </template>
                <template v-else>
                  You've been invited to compete in <b class="emphasis">{{ entityName }}</b>
                </template>
              </template>
              <template v-else-if="inviteLink.referenceType === 2">
                <template v-if="clubName && !alreadyInClub">
                  You've been invited to join <b class="emphasis">{{ clubName }}</b> and compete in <b class="emphasis">{{ entityName }}</b>
                </template>
                <template v-else>
                  You've been invited to compete in <b class="emphasis">{{ entityName }}</b>
                </template>
              </template>
              <template v-else-if="inviteLink.referenceType === 4">
                <template v-if="clubName && !alreadyInClub">
                  You've been invited to join <b class="emphasis">{{ clubName }}</b> and compete in the <b class="emphasis">{{ entityName }}</b> tournament
                </template>
                <template v-else>
                  You've been invited to compete in the <b class="emphasis">{{ entityName }}</b> tournament
                </template>
              </template>
              <template v-else-if="inviteLink.referenceType === 5">
                <template v-if="clubName && !alreadyInClub">
                  You've been invited to join <b class="emphasis">{{ clubName }}</b> and take part in the <b class="emphasis">{{ entityName }}</b> meet
                </template>
                <template v-else>
                  You've been invited to take part in the <b class="emphasis">{{ entityName }}</b> meet
                </template>
              </template>
              <template v-else-if="inviteLink.referenceType === 6">
                <template v-if="clubName && !alreadyInClub">
                  You've been invited to join <b class="emphasis">{{ clubName }}</b> and compete in <b class="emphasis">{{ entityName }}</b>
                </template>
                <template v-else>
                  You've been invited to compete in <b class="emphasis">{{ entityName }}</b>
                </template>
              </template>
            </p>
            <template v-if="isUnpublished || requestSent"/>
            <p v-else-if="isAuthenticated && !isAlreadyMember">Click below to accept your invitation.</p>
            <p v-else-if="needsPasswordUnlock">Enter the password below to continue.</p>
            <p v-else-if="!isAuthenticated">Sign in or create an account to continue.</p>
          </template>
        </template>

        <!-- Join message from admin -->
        <v-alert v-if="inviteLink.joinMessage" type="info" variant="tonal" class="mt-2 mb-3" density="compact">
          {{ inviteLink.joinMessage }}
        </v-alert>

        <!-- Draft target: nothing here can succeed until the club publishes it -->
        <template v-if="isUnpublished">
          <v-alert type="warning" variant="tonal" density="comfortable" class="mt-2" data-testid="invite-unpublished">
            <div class="text-body-1 font-weight-bold">Not published yet</div>
            <div class="text-body-2">
              {{ entityName }} hasn't been published yet, so it can't be joined. Try this link again once the club
              has published it.
            </div>
          </v-alert>
        </template>

        <!-- Password unlock section (not authenticated, password protected, no authId yet) -->
        <template v-else-if="needsPasswordUnlock">
          <div class="tl-label mb-1 mt-2">Password</div>
          <v-text-field
            v-model="password"
            density="compact"
            type="password"
            placeholder="••••••••"
            prepend-inner-icon="mdi-lock-outline"
            hide-details="auto"
          />
          <div class="auth-help">This invite link is password protected.</div>
          <crud-error-display :errors="unlockError" class="mt-3"/>
          <v-btn
            color="primary"
            size="large"
            :block="true"
            class="mt-5"
            :loading="unlockPending"
            @click="handleUnlock"
            :disabled="!password"
          >
            Unlock
          </v-btn>
        </template>

        <!-- Auth forms section (not authenticated, has authId) -->
        <template v-else-if="canShowAuthForms">
          <!-- New or existing? Nothing is preselected. Once chosen, the cards go
               and a back control + a switch link take their place. -->
          <!-- New or existing? Two accordion cards, nothing preselected: the chosen
               one opens with its form inside, the other is always the way across. -->
          <auth-chooser v-model="inviteChoice" class="mt-2">
            <template #signIn>
              <login-form
                :auth-id="authId"
                :hide-hero="true"
                :redirect-url="route.fullPath"
              />
            </template>
            <template #signUp>
              <register-form
                :auth-id="authId"
                :hide-hero="true"
                :redirect-url="route.fullPath"
                sent-note="The link brings you back to this invite."
              />
              <div class="auth-note">Accepting creates a free Tap Leagues account.</div>
            </template>
          </auth-chooser>
        </template>

        <!-- Authenticated join section -->
        <template v-else-if="isAlreadyMember">
          <div class="already-in" data-testid="invite-already-member">
            <v-icon size="22" color="success">{{ isAwaitingPlacement ? 'mdi-account-clock' : 'mdi-check-circle' }}</v-icon>
            <div>
              <div class="text-body-1 font-weight-bold">{{ isAwaitingPlacement ? "You're in, waiting to be placed" : "You're already in" }}</div>
              <div class="text-body-2 text-medium-emphasis">
                <template v-if="isAwaitingPlacement">
                  You've joined {{ entityName }}<template v-if="clubName"> at {{ clubName }}</template>. An admin will place you and we'll let you know.
                  <template v-if="entryFeeMinor != null"> {{ heldJoinFeeLine(feeAmount) }}</template>
                </template>
                <template v-else>
                  Your account is part of {{ entityName }}<template v-if="clubName"> at {{ clubName }}</template>, so there's nothing more to accept.
                </template>
              </div>
            </div>
          </div>
          <v-btn color="primary" size="large" :block="true" class="mt-4" data-testid="invite-open-entity" @click="goToEntity">
            Open {{ entityTypeText?.toLowerCase() || 'page' }}
          </v-btn>
        </template>

        <template v-else-if="isAuthenticated">
          <!-- Name mismatch warning for player invites -->
          <v-alert
            v-if="namesMismatch"
            type="warning"
            variant="tonal"
            class="mb-4"
            density="comfortable"
          >
            <div class="text-body-2">
              <strong>Name Mismatch Detected</strong>
              <p class="mt-2 mb-0">
                This invitation is for <strong>{{ inviteLink.player.fullName }}</strong>,
                but you're logged in as <strong>{{ user.firstName }} {{ user.lastName }}</strong>.
              </p>
              <p class="mt-2 mb-0">
                Please confirm this is correct before proceeding.
              </p>
            </div>
            <v-checkbox
              v-model="nameConfirmed"
              class="mt-3"
              density="compact"
              hide-details
            >
              <template v-slot:label>
                <span class="text-body-2">I confirm this invitation is for me</span>
              </template>
            </v-checkbox>
          </v-alert>

          <!-- The request already exists (sent by signing in or registering here, or earlier) -->
          <v-alert v-if="requestSent" type="success" variant="tonal" density="comfortable" class="mb-4"
                   data-testid="invite-request-sent">
            <div class="text-body-1 font-weight-bold">Your request to join has been sent</div>
            <div class="text-body-2">
              An admin will review it shortly and we'll let you know. You can add a message for them below.
            </div>
          </v-alert>

          <!-- Message field for admin approval requests -->
          <template v-if="requiresApproval">
            <div class="tl-label mb-1 mt-2">Message <span class="optional">(optional)</span></div>
            <v-textarea
              v-model="requestMessage"
              density="compact"
              placeholder="Add a message to your join request"
              rows="2"
              auto-grow
              hide-details="auto"
              class="mb-4"
            />
          </template>

          <!-- Entry fee: summary. Paid interactively AFTER joining (or approval). -->
          <template v-if="isPaidJoin">
            <div class="entry-fee-box mb-4">
              <div class="d-flex align-center justify-space-between">
                <span class="ef-label">Entry fee</span>
                <span class="ef-amount">{{ money(entryFeeMinor, entryFeeCurrency) }}</span>
              </div>
              <div class="ef-note">
                <template v-if="asksJoinTerms">
                  {{ entryFeeJoinNote({requiresApproval, entity: entityTypeText?.toLowerCase() || 'competition', held: joinHeld, amount: feeAmount}) }}
                </template>
                <template v-else-if="requiresApproval">
                  You'll be able to pay once an admin approves your request.
                </template>
                <template v-else-if="joinHeld">
                  {{ heldJoinFeeLine(feeAmount) }}
                </template>
                <template v-else>
                  You'll be prompted to pay right after joining, or later from the
                  {{ entityTypeText?.toLowerCase() || 'competition' }} page.
                </template>
                
              </div>
              <div v-if="joinPayee" class="ef-payee" data-testid="invite-entry-fee-payee">{{ joinPayee }}</div>
              <div class="ef-wallets">Apple Pay, Google Pay and cards accepted.</div>
            </div>
          </template>

          <!-- Questions about this competition, answered as part of joining -->
          <div v-if="joinQuestions.length" class="join-questions mb-4" data-testid="invite-competition-questions">
            <competition-question-list v-model="joinTicks" :questions="joinQuestions" :disabled="joinPending"/>
          </div>

          <!-- Join and pay: the terms and the tick that commits them, right above the button -->
          <div v-if="asksJoinTerms" class="ef-terms mb-4" data-testid="invite-entry-fee-terms">
            <div class="tl-label">Entry fee terms</div>
            <div class="ef-terms-box" v-html="termsAsHtml(joinTerms.termsHtml)"/>
            <p v-if="joinCancellation" class="ef-cancellation" data-testid="invite-entry-fee-cancellation">{{ joinCancellation }}</p>
            <v-checkbox v-model="entryFeeTicked" density="compact" hide-details class="mt-2"
                        :disabled="joinPending" data-testid="invite-entry-fee-tick">
              <template #label>
                <span class="ef-terms-tick">{{ entryFeeJoinTickLabel(feeAmount, joinHeld && !requiresApproval) }}</span>
              </template>
            </v-checkbox>
          </div>

          <!-- Join errors -->
          <crud-error-display :errors="joinError" class="mb-4"/>

          <!-- Join button -->
          <v-btn
            color="primary"
            size="large"
            :block="true"
            class="mt-2"
            :loading="joinPending"
            :disabled="(namesMismatch && !nameConfirmed) || (requestSent && !requestMessage.trim())
                       || entryFeeJoinBlocked({asksTerms: asksJoinTerms, ticked: entryFeeTicked})"
            data-testid="invite-join"
            @click="handleJoin"
          >
            {{ joinButtonLabel }}
          </v-btn>
          <v-btn v-if="requestSent" variant="text" size="large" :block="true" class="mt-2"
                 data-testid="invite-open-entity" @click="goToEntity">
            Open {{ entityTypeText?.toLowerCase() || 'page' }}
          </v-btn>
        </template>
      </auth-card>

      <pay-entry-fee-dialog v-model="payFeeOpen" :obligation="payFeeObligation" @paid="onFeePaid"
                            @update:model-value="(v) => { if (!v) goToEntity(); }"/>

      <!-- What joining gives you (static, from invite.html) -->
      <div v-if="inviteLink && !hasInviteLinkErrors && !inviteLinkPending" class="tl-card perks-card">
        <div class="pa-4 pb-0">
          <div class="tl-label">What joining gives you</div>
        </div>
        <div class="perk-row">
          <div class="pic"><v-icon size="18" icon="mdi-trophy-outline"/></div>
          <div class="who">
            <div class="t">Play in club competitions</div>
            <div class="s">Box leagues, the club ladder and tournaments.</div>
          </div>
        </div>
        <div class="perk-row">
          <div class="pic"><v-icon size="18" icon="mdi-scoreboard-outline"/></div>
          <div class="who">
            <div class="t">Record scores in seconds</div>
            <div class="s">Standings and stats update for everyone instantly.</div>
          </div>
        </div>
        <div class="perk-row">
          <div class="pic"><v-icon size="18" icon="mdi-chat-outline"/></div>
          <div class="who">
            <div class="t">Message your opponents</div>
            <div class="s">Arrange matches without sharing your phone number.</div>
          </div>
        </div>
      </div>
    </v-container>
    <!-- Already in, typically because they joined while confirming their email
         after registering here, so the join ticks above were never shown. Ask
         now, on the page they land on (UAT, 27 Sep 2026). Someone waiting to be
         placed counts as in it (API CompetitionQuestionService). -->
    <competition-questions-prompt
        v-if="isAlreadyMember && joinCompetitionType"
        :competition-type="joinCompetitionType"
        :competition-id="inviteLink.referenceId"/>
  </base-page>
</template>

<style scoped lang="scss">
/* competition questions, answered as part of joining */
.join-questions {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 14px;
}

/* Join and pay: the entry fee terms and their tick */
.ef-terms-box {
  max-height: 200px;
  overflow-y: auto;
  font-size: 12.5px;
  line-height: 1.45;
  background: rgb(var(--v-theme-light));
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 4px;
}

.ef-terms-tick {
  font-size: 13px;
}

.ef-cancellation {
  font-size: 12.5px;
  line-height: 1.45;
  margin-top: 8px;
}

.ef-payee {
  margin-top: 6px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
}

/* entry-fee summary box on the join card */
.entry-fee-box {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 14px;
  background: rgb(var(--v-theme-light));
}

.ef-label {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
}

.ef-amount {
  font-size: 18px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

.ef-note {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 4px;
}

.ef-card-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 8px 10px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.ef-wallets {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 6px;
  text-align: center;
}

/* club identity crest (invite.html) */
.invite-crest {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
  margin: 0 auto 14px;
  overflow: hidden;

  &.image {
    background: rgb(var(--v-theme-light));
    border: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.invite-chips {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.invite-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border-radius: 999px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-light));
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
}

:deep(.auth-hero) .emphasis {
  color: rgb(var(--v-theme-text));
  font-weight: 600;
}

.optional {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.perks-card {
  max-width: 440px;
  margin: 16px auto 0;
}

.perk-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;

  + .perk-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  .pic {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .who {
    flex: 1;
    min-width: 0;

    .t {
      font-size: 13px;
      font-weight: 600;
    }

    .s {
      font-size: 11px;
      color: rgb(var(--v-theme-textSubtle));
      margin-top: 1px;
      line-height: 1.5;
    }
  }
}
.already-in {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border: 1px solid rgb(var(--v-theme-success));
  border-radius: 12px;
  background: rgba(var(--v-theme-success), 0.06);
}
</style>
